import { useCallback, useEffect, useState, type ChangeEvent } from 'react'
import { ThickArrowLeftIcon, ThickArrowRightIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'

import { PaytrieAPI } from '@paytrie/client'
import { usePageStateStore } from '@/reducers/'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { TransactionRow } from './TransactionRows'
import { TransactionCard } from './TransactionCard'
import { ReportButton } from './ReportButton'
import {
  DEFAULT_PAGE_SIZE,
  getPageNumbers,
  PAGE_SIZE_OPTIONS,
} from './pagination'
import Sidebar from './Sidebar'
import Topbar from './Topbar'
import { HistoryTabs } from './HistoryTabs'

export const DashboardHistory = () => {
  const { t } = useTranslation()
  const authToken = usePageStateStore.use.authToken() || ''
  const { data: currentUser } = useCurrentUser(!!authToken)
  const firstName = currentUser?.firstName ?? ''
  const [transactions, setTransactions] = useState<PaytrieAPI.Transaction[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await PaytrieAPI.listTransactions({
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      })
      if (response.status === 200) {
        setTransactions(response.data.data.items)
        setTotalCount(response.data.data.total)
      }
    } catch {
      console.warn('Unable to fetch transactions')
    }
  }, [currentPage, pageSize])

  const refreshTransactions = fetchTransactions

  useEffect(() => {
    if (authToken.length > 0) {
      void fetchTransactions()
    }
  }, [authToken, fetchTransactions])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const pageNumbers = getPageNumbers(currentPage, totalPages)

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(event.target.value))
    setCurrentPage(1)
  }

  return (
    <div
      className="relative flex min-h-screen flex-col md:flex-row"
      data-testid="container-dashboard-history-container"
    >
      <Topbar />
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black opacity-70 z-30"
          onClick={toggleSidebar}
          data-testid="nav-overlay"
        ></div>
      )}
      <div className="flex-grow bg-cloud dark:bg-pebble p-6 relative z-10">
        <div
          className="bg-cloud dark:bg-pebble rounded-lg flex-grow pt-16 lg:min-w-[1050px] lg:ml-[-155px] pb-20"
          data-testid="container-content-container"
        >
          <div className="flex flex-row items-center justify-between py-2">
            <h1
              className="text-3xl font-normal text-left mb-4 text-black dark:text-white"
              data-testid="container-title"
            >
              {t('dashboardHistory.title')}
            </h1>
            <div className="mb-4 lg:text-base text-xs">
              <ReportButton firstName={firstName} />
            </div>
          </div>
          <HistoryTabs active="transactions" />
          <div
            className="flex flex-col gap-3 lg:hidden"
            data-testid="list-transaction-cards"
          >
            {transactions.map((t, index) => (
              <TransactionCard
                key={t.id}
                index={index}
                transactionId={t.id}
                status={t.status}
                date={t.date ?? ''}
                leftSideLabel={t.leftSideLabel}
                leftSideValue={t.leftSideValue}
                rightSideLabel={t.rightSideLabel}
                rightSideValue={t.rightSideValue}
              />
            ))}
          </div>
          <div
            className="hidden overflow-x-auto lg:block"
            data-testid="text-table-container"
          >
            <table
              className="min-w-full bg-cloud dark:bg-pebble border-collapse"
              data-testid="item-table"
            >
              <thead>
                <tr data-testid="text-table-header-row">
                  <th
                    className="py-2 lg:text-xl sm:text-sm border-b-2 border-gray-300 dark:border-gray-700 text-left dark:text-gray-300"
                    data-testid="text-header-date"
                  >
                    {t('dashboardHistory.table.headers.date')}
                  </th>
                  <th
                    className="py-2 lg:text-xl sm:text-sm border-b-2 border-gray-300 dark:border-gray-700 text-left dark:text-gray-300"
                    data-testid="text-header-id"
                  >
                    {t('dashboardHistory.table.headers.transactionId')}
                  </th>
                  <th
                    className="py-2 lg:text-xl sm:text-sm border-b-2 border-gray-300 dark:border-gray-700 text-left dark:text-gray-300"
                    data-testid="text-header-from"
                  >
                    {t('dashboardHistory.table.headers.from')}
                  </th>
                  <th
                    className="py-2 lg:text-xl sm:text-sm border-b-2 border-gray-300 dark:border-gray-700 text-left dark:text-gray-300"
                    data-testid="text-header-to"
                  >
                    {t('dashboardHistory.table.headers.to')}
                  </th>
                  <th
                    className="py-2 lg:text-xl sm:text-sm border-b-2 border-gray-300 dark:border-gray-700 text-center dark:text-gray-300"
                    data-testid="text-header-status"
                  >
                    {t('dashboardHistory.table.headers.status')}
                  </th>
                </tr>
              </thead>
              <tbody data-testid="text-table-body">
                {transactions.map((t, index) => (
                  <TransactionRow
                    key={t.id}
                    index={index}
                    transactionId={t.id}
                    status={t.status}
                    rightSideLabel={t.rightSideLabel}
                    rightSideValue={t.rightSideValue}
                    leftSideLabel={t.leftSideLabel}
                    leftSideValue={t.leftSideValue}
                    date={t.date ?? ''}
                    wallet={t.wallet}
                    paymentId={t.paymentId ?? ''}
                    refreshTransactions={refreshTransactions}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <footer
          className="w-full bg-cloud dark:bg-pebble p-4 flex flex-col items-center fixed bottom-0 left-0 z-20"
          data-testid="container-footer"
        >
          <div
            className="flex justify-center items-center space-x-4 mb-2"
            data-testid="container-controls"
          >
            <label
              className="flex items-center space-x-2 text-gray-900 dark:text-gray-100"
              data-testid="container-page-size"
            >
              <span>{t('dashboardHistory.pagination.perPage')}</span>
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                className="border border-gray-300 dark:border-gray-700 rounded bg-cloud dark:bg-pebble px-2 py-1"
                data-testid="select-page-size"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <button
              onClick={handlePreviousPage}
              className="p-2 rounded-full disabled:opacity-50"
              disabled={currentPage === 1}
              data-testid="nav-prev-button"
            >
              <ThickArrowLeftIcon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
            </button>
            <div
              className="flex items-center space-x-1"
              data-testid="container-page-numbers"
            >
              {pageNumbers.map((page, idx) =>
                page === 'ellipsis' ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="px-2 text-gray-900 dark:text-gray-100"
                    data-testid="page-ellipsis"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 rounded ${
                      page === currentPage
                        ? 'bg-black text-white dark:bg-white dark:text-black'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}
                    aria-current={page === currentPage ? 'page' : undefined}
                    data-testid={`nav-page-${page}`}
                  >
                    {page}
                  </button>
                )
              )}
            </div>
            <button
              onClick={handleNextPage}
              className="p-2 rounded-full disabled:opacity-50"
              disabled={currentPage === totalPages}
              data-testid="nav-next-button"
            >
              <ThickArrowRightIcon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

export default DashboardHistory

/*
POTENTIAL IMPROVEMENTS:
1. Implement proper error handling and loading states for transaction fetching
2. Add filtering capabilities for transaction history
3. Implement sorting functionality for columns
4. Consider adding a search function for transactions
5. Add proper empty state for when no transactions are available
6. Consider virtualizing the transaction table for better performance with large datasets
7. Add proper error boundaries for catching rendering errors
8. Consider adding export functionality for transaction history
9. Implement proper date formatting and localization for transaction dates
10. Add better pagination controls with the ability to jump to specific pages
11. Implement proper accessibility for the transaction table (ARIA roles, keyboard navigation)
*/
